import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  INVENTORY_HEARTBEAT_MS,
  INVENTORY_STAGE_LABELS,
  LINE_RESULT_LABELS,
  roleCanForceUnlock,
  roleCanInventory,
  roleCanManageReplacements,
  roleCanOverrideInventory,
  SESSION_STATUS_LABELS,
  type InventorySessionResponse,
  type LineResult,
  type PublicInventoryLine,
  type PublicInventorySession,
  type QrScan,
} from '@inventohub/shared';
import { HelpLink } from '../components/HelpLink';
import { QrScanPanel } from '../components/QrScanPanel';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import { readImageAsBase64 } from '../lib/files';
import { lookupErrorMessage, resolveQrScan } from '../lib/qr';

export function InventorySessionPage(): JSX.Element {
  const { user, code } = useAuth();
  const params = useParams();
  const sessionId = params.sessionId ?? '';
  const [session, setSession] = useState<PublicInventorySession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [extraSerial, setExtraSerial] = useState('');
  const [busy, setBusy] = useState(false);

  const canEditRole = Boolean(code || (user && roleCanInventory(user.role)));
  const canForce = user ? roleCanForceUnlock(user.role) : false;
  const canOverride = user ? roleCanOverrideInventory(user.role) : false;
  const finalized = session?.status === 'finalized';
  const youHoldIt = Boolean(session?.lock.youHoldIt);
  const lockedOut = Boolean(session?.lock.held && !session.lock.youHoldIt);
  const canEdit = Boolean(
    canEditRole && session && !lockedOut && (!finalized || canOverride) && youHoldIt,
  );

  const load = useCallback(async (): Promise<PublicInventorySession | null> => {
    const body = await apiRequest<InventorySessionResponse>(`/api/inventory/sessions/${sessionId}`);
    let current = body.session;
    if (code || (user && roleCanInventory(user.role))) {
      try {
        const claimed = await apiRequest<InventorySessionResponse>('/api/inventory/sessions', {
          method: 'POST',
          body: {
            toteId: current.toteId,
            stage: current.stage,
            loadoutId: current.loadoutId,
          },
        });
        current = claimed.session;
      } catch (caught: unknown) {
        if (caught instanceof ApiClientError && caught.httpStatus === 409) {
          setError(caught.message);
        } else {
          throw caught;
        }
      }
    }
    setSession(current);
    return current;
  }, [sessionId, user, code]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await load();
        if (cancelled || !loaded) return;
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load this session.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (!youHoldIt || !sessionId) return;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const body = await apiRequest<InventorySessionResponse>(
            `/api/inventory/sessions/${sessionId}/heartbeat`,
            { method: 'POST' },
          );
          setSession(body.session);
        } catch (caught: unknown) {
          if (caught instanceof ApiClientError && caught.httpStatus === 409) {
            setError(caught.message);
            try {
              await load();
            } catch {
              /* keep the 409 message */
            }
          }
        }
      })();
    }, INVENTORY_HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [youHoldIt, sessionId, load]);

  function updateLine(lineId: string, patch: Partial<PublicInventoryLine>): void {
    setSession((current) =>
      current
        ? {
            ...current,
            lines: current.lines.map((line) => (line.id === lineId ? { ...line, ...patch } : line)),
          }
        : current,
    );
  }

  async function save(submit: boolean): Promise<void> {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const body = await apiRequest<InventorySessionResponse>(
        `/api/inventory/sessions/${session.id}`,
        {
          method: 'PATCH',
          body: {
            version: session.version,
            submit,
            notes: session.notes,
            lines: session.lines.map((line) => ({
              id: line.id,
              result: line.result,
              qtyPresent: line.qtyPresent,
              notes: line.notes,
            })),
          },
        },
      );
      setSession(body.session);
      setNotice(submit ? 'Submitted. You can still edit until finalize.' : 'Saved.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save.');
      if (caught instanceof ApiClientError && caught.httpStatus === 409) await load();
    } finally {
      setBusy(false);
    }
  }

  async function finalize(): Promise<void> {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const body = await apiRequest<InventorySessionResponse>(
        `/api/inventory/sessions/${session.id}/finalize`,
        { method: 'POST', body: { version: session.version } },
      );
      setSession(body.session);
      setNotice('Finalized. Lines are read-only except for an equipment-admin override.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not finalize.');
      if (caught instanceof ApiClientError && caught.httpStatus === 409) await load();
    } finally {
      setBusy(false);
    }
  }

  async function onForceUnlock(): Promise<void> {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const body = await apiRequest<InventorySessionResponse>(
        `/api/inventory/sessions/${session.id}/force-unlock`,
        { method: 'POST', body: {} },
      );
      setSession(body.session);
      setNotice('Lock cleared.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not force-unlock.');
    } finally {
      setBusy(false);
    }
  }

  async function addExtra(serial: string): Promise<void> {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const body = await apiRequest<InventorySessionResponse>(
        `/api/inventory/sessions/${session.id}/extras`,
        {
          method: 'POST',
          body: { serial, version: session.version },
        },
      );
      setSession(body.session);
      setExtraSerial('');
      setNotice('Extra recorded. Official tote membership is unchanged.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not add that extra.');
      if (caught instanceof ApiClientError && caught.httpStatus === 409) await load();
    } finally {
      setBusy(false);
    }
  }

  async function onExtraScan(scan: QrScan): Promise<void> {
    setError(null);
    try {
      if (scan.kind === 'tote') {
        setError('That QR is a tote. Type or scan an asset serial for an extra.');
        return;
      }
      const found = await resolveQrScan(scan);
      if (!found) {
        setError('No asset matches that QR or typed serial.');
        return;
      }
      if (found.kind !== 'asset') {
        setError('That QR is a tote. Type or scan an asset serial for an extra.');
        return;
      }
      setExtraSerial(found.asset.serial);
      await addExtra(found.asset.serial);
    } catch (caught: unknown) {
      setError(lookupErrorMessage(caught, 'Could not add that extra.'));
    }
  }

  async function onPhoto(line: PublicInventoryLine, file: File | undefined): Promise<void> {
    if (!file || !session) return;
    try {
      const image = await readImageAsBase64(file);
      await apiRequest(`/api/inventory/sessions/${session.id}/lines/${line.id}/photos`, {
        method: 'POST',
        body: image,
      });
      await load();
      setNotice('Photo attached.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not attach the photo.');
    }
  }

  const expected = session?.lines.filter((line) => line.expected) ?? [];
  const extras = session?.lines.filter((line) => !line.expected) ?? [];

  return (
    <section data-testid="inventory-session">
      <div className="card">
        <p className="meta">
          <Link to={session ? `/totes/${encodeURIComponent(session.tote.number)}` : '/totes'}>
            Tote {session?.tote.number ?? ''}
          </Link>
        </p>
        <div className="card__title-row">
          <h2>
            {session ? INVENTORY_STAGE_LABELS[session.stage] : 'Inventory'}
            {session ? ` · ${SESSION_STATUS_LABELS[session.status]}` : ''}
          </h2>
          <HelpLink slug="inventory" />
        </div>
        {session?.loadout ? (
          <p className="meta">
            {session.loadout.events.map((event) => event.name).join(' + ') ||
              session.loadout.name ||
              'Attached loadout'}
          </p>
        ) : (
          <p className="meta">Shop general — not attached to a loadout.</p>
        )}
        {lockedOut ? (
          <p className="status status--warn" data-testid="lock-holder">
            {session?.lock.holder?.name ?? 'Someone'} holds the lock on this tote.
          </p>
        ) : null}
        {youHoldIt ? (
          <p className="meta" data-testid="you-hold-lock">
            You hold the lock. Heartbeat every 60 seconds while this screen is open.
          </p>
        ) : null}
        {finalized ? (
          <p className="status status--ok" data-testid="finalized-banner">
            Finalized. Staff and tournament directors cannot edit lines.
          </p>
        ) : null}
        {error ? (
          <p className="status status--error" data-testid="inventory-error">
            {error}
          </p>
        ) : null}
        {notice ? <p className="status status--ok">{notice}</p> : null}
      </div>

      {session
        ? expected.map((line) => (
            <LineCard
              key={line.id}
              line={line}
              disabled={!canEdit}
              session={session}
              canRequestReplacement={Boolean(user && roleCanManageReplacements(user.role))}
              onResult={(result) => updateLine(line.id, { result })}
              onQty={(qtyPresent) => updateLine(line.id, { qtyPresent })}
              onNotes={(notes) => updateLine(line.id, { notes })}
              onPhoto={(file) => void onPhoto(line, file)}
            />
          ))
        : null}

      {extras.length > 0 ? (
        <div className="card">
          <h2>Extras</h2>
          <p className="meta">
            On-site spares are recorded here. They do not change official tote membership.
          </p>
          {extras.map((line) => (
            <p key={line.id} data-testid={`extra-${line.serial ?? line.id}`}>
              {line.serial ?? 'Unknown'} · {LINE_RESULT_LABELS[line.result ?? 'extra']}
            </p>
          ))}
        </div>
      ) : null}

      {canEdit ? (
        <div className="card">
          <h2>Unexpected extra</h2>
          <QrScanPanel
            label="Serial or QR"
            value={extraSerial}
            onValueChange={setExtraSerial}
            onScan={(scan) => void onExtraScan(scan)}
            submitLabel="Add extra"
            inputTestId="extra-serial"
            disabled={busy}
          />
        </div>
      ) : null}

      {canEdit ? (
        <div className="card inventory-actions">
          <button
            type="button"
            className="button"
            disabled={busy}
            data-testid="save-inventory"
            onClick={() => void save(false)}
          >
            Save
          </button>
          <button
            type="button"
            className="button button--ghost"
            disabled={busy}
            data-testid="submit-inventory"
            onClick={() => void save(true)}
          >
            Submit tote
          </button>
          <button
            type="button"
            className="button button--ghost"
            disabled={busy}
            data-testid="finalize-inventory"
            onClick={() => void finalize()}
          >
            Finalize
          </button>
        </div>
      ) : null}

      {canForce && session && session.lock.held ? (
        <div className="card">
          <button
            type="button"
            className="button button--ghost"
            disabled={busy}
            data-testid="force-unlock"
            onClick={() => void onForceUnlock()}
          >
            Force-unlock
          </button>
        </div>
      ) : null}
    </section>
  );
}

function LineCard({
  line,
  disabled,
  session,
  canRequestReplacement,
  onResult,
  onQty,
  onNotes,
  onPhoto,
}: {
  line: PublicInventoryLine;
  disabled: boolean;
  session: PublicInventorySession;
  canRequestReplacement: boolean;
  onResult: (result: LineResult) => void;
  onQty: (qty: number | null) => void;
  onNotes: (notes: string | null) => void;
  onPhoto: (file: File | undefined) => void;
}): JSX.Element {
  const consumable = Boolean(line.consumableTypeId);
  const label = consumable
    ? `${line.consumableName ?? 'Consumable'} (expect ${line.qtyExpected ?? 0})`
    : (line.serial ?? 'Asset');

  return (
    <div className="card" data-testid={`line-${line.id}`}>
      <h2>{label}</h2>
      {line.updatedByName ? (
        <p className="meta" data-testid={`line-${line.id}-actor`}>
          Last saved by {line.updatedByName}
        </p>
      ) : null}
      {line.assetStatus === 'missing' || line.assetStatus === 'broken' ? (
        <p className="status status--warn">Asset status is already {line.assetStatus}.</p>
      ) : null}
      {canRequestReplacement &&
      line.assetId &&
      (line.result === 'missing' ||
        line.result === 'broken' ||
        line.assetStatus === 'missing' ||
        line.assetStatus === 'broken') ? (
        <p>
          <Link
            className="button button--ghost"
            to={`/replacements/new?kind=${line.result === 'broken' || line.assetStatus === 'broken' ? 'broken' : 'missing'}&assetId=${line.assetId}&toteId=${session.toteId}${session.loadoutId ? `&loadoutId=${session.loadoutId}` : ''}${session.loadout?.events[0] ? `&eventId=${session.loadout.events[0].id}` : ''}&inventoryLineId=${line.id}`}
            data-testid={`line-${line.id}-replacement`}
          >
            Request replacement
          </Link>
        </p>
      ) : null}
      {consumable ? (
        <label className="field">
          Quantity present
          <input
            type="number"
            min="0"
            value={line.qtyPresent ?? ''}
            disabled={disabled}
            onChange={(event) =>
              onQty(event.target.value === '' ? null : Number(event.target.value))
            }
            data-testid={`line-${line.id}-qty`}
          />
        </label>
      ) : (
        <div className="result-row" role="group" aria-label={`Result for ${label}`}>
          {(['present', 'missing', 'broken'] as const).map((result) => (
            <button
              key={result}
              type="button"
              disabled={disabled}
              className={`button result-button result-button--${result}${line.result === result ? ' result-button--selected' : ''}`}
              data-testid={`line-${line.id}-${result}`}
              onClick={() => onResult(result)}
            >
              {LINE_RESULT_LABELS[result]}
            </button>
          ))}
        </div>
      )}
      <label className="field">
        Note
        <input
          value={line.notes ?? ''}
          disabled={disabled}
          onChange={(event) => onNotes(event.target.value || null)}
        />
      </label>
      {disabled ? null : (
        <label className="field">
          Photo
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => onPhoto(event.target.files?.[0])}
          />
        </label>
      )}
    </div>
  );
}
